/**
 * Single source of truth for who can do what in the Schedule module.
 *
 * The rules come from the role x privilege matrix below. Roles live in `user.role`
 * ('Teacher', 'Data Operator', 'Operation', 'Student'); privileges live in
 * `user.privilage` ('SuperAdmin', 'Admin', 'Planner', 'AttendanceMarker', ...).
 * They are separate lists — a Teacher can also hold the Planner privilege — so every
 * cell is a (role, privilege) pair, not one or the other.
 *
 * Transcribed cell for cell from the Role & Privilege Matrix document. Columns are
 * View / Creation / Resources / Quizzes / Attendance.
 *
 *   OPERATION
 *     none             -    -      -     -     -      (module hidden entirely)
 *     Admin            all  yes    yes   yes   all
 *     AttendanceMarker all  no     no    no    all
 *     Planner          all  yes    no    no    all
 *
 *   DATA OPERATOR
 *     none             all  no     yes   yes   no
 *     Admin            all  yes    yes   yes   all
 *     AttendanceMarker all  no     yes   yes   all
 *     Planner          all  yes    yes   yes   all
 *
 *   TEACHER
 *     none             own  no     own   own   own classes, only while ongoing
 *     Admin            all  yes    own   own   all
 *     AttendanceMarker all  no     all   all   all
 *     Planner          all  yes    own   own   all
 *
 *   SUPER ADMIN        all  yes    all   all   all      (every mode)
 *
 * Three things in that table are deliberate and easy to mistake for bugs:
 *   - Operation is more restricted than Data Operator. Operation + AttendanceMarker and
 *     Operation + Planner get NO resources or quizzes, where the same privileges on a
 *     Data Operator grant them.
 *   - A Teacher's resources/quizzes stay on their OWN classes even when they hold Admin
 *     or Planner. AttendanceMarker is the one privilege that widens them to all classes
 *     (Teacher row 3 reads "Resources: Yes", not "Yes — own classes"). That is what
 *     `contentScope` below expresses.
 *   - Nothing in the matrix is view-only. A "Yes" in Resources or Quizzes is full
 *     access; ACCESS.VIEW survives only for the Teacher-looking-at-someone-else's-class
 *     case, which callers derive from `contentScope`, not from a role.
 */

const NONE = 'none';
const VIEW = 'view';
const EDIT = 'edit';

export const ACCESS = { NONE, VIEW, EDIT };

// Attendance is three-valued rather than a boolean: a plain Teacher may only mark their
// own class, and only while it is ongoing.
export const ATTENDANCE = { NONE: 'none', ALL: 'all', OWN_ONGOING: 'own-ongoing' };

const asList = (value) =>
  (Array.isArray(value) ? value : [])
    .map((entry) => String(entry || '').trim().toLowerCase())
    .filter(Boolean);

export const getSchedulePermissions = (user) => {
  const roles = asList(user?.role);
  const privileges = asList(user?.privilage);

  const has = (list, name) => list.includes(name);

  const isSuperAdmin = has(privileges, 'superadmin') || user?.isSuperAdmin === true;
  const isAdmin = has(privileges, 'admin') || user?.isAdmin === true;
  const isPlanner = has(privileges, 'planner') || user?.isPlanner === true;
  const isAttendanceMarker = has(privileges, 'attendancemarker') || user?.isAttendanceMarker === true;

  const isTeacher = has(roles, 'teacher');
  const isDataOperator = has(roles, 'data operator') || has(roles, 'dataoperator');
  const isOperation = has(roles, 'operation') || has(roles, 'operations');
  const isStudent = has(roles, 'student');

  // "Elevated" means any privilege that lifts a user out of their bare role. It is the
  // hinge for module visibility, view scope and attendance.
  const hasElevatedPrivilege = isSuperAdmin || isAdmin || isPlanner || isAttendanceMarker;

  // Students are untouched by any of this — they have their own schedule view.
  // A bare Operation user with no privileges has the module hidden entirely.
  const canViewModule = hasElevatedPrivilege || isTeacher || isDataOperator || isStudent || !isOperation;

  // Identical across all three tables: Admin and Planner create, AttendanceMarker does
  // not, and a bare role does not.
  const canCreate = isSuperAdmin || isAdmin || isPlanner;

  // A bare Teacher sees only their own classes; any elevated privilege widens that to
  // every class. Everyone else already sees everything.
  const viewScope = isTeacher && !hasElevatedPrivilege ? 'own' : 'all';

  const contentAccess = (() => {
    if (isSuperAdmin) return EDIT;
    // Checked before the privilege ladder: a Teacher keeps content rights at every
    // privilege level, including none. How far those rights reach is contentScope's job.
    if (isTeacher) return EDIT;
    // Every Data Operator row in the matrix says yes to Resources and Quizzes, the
    // no-privilege row included.
    if (isDataOperator) return EDIT;
    // Operation is the restricted one: only Admin grants content. Planner and
    // AttendanceMarker explicitly do not.
    if (isOperation) return isAdmin ? EDIT : NONE;
    return isAdmin || isPlanner || isAttendanceMarker ? EDIT : NONE;
  })();

  // How far those content rights reach. Only a Teacher is ever narrowed to their own
  // classes. The matrix previously said AttendanceMarker lifts this narrowing, but
  // recent requirements state that Teacher + AttendanceMarker should still only see
  // resources/quizzes for their own classes.
  const contentScope = !isTeacher || isSuperAdmin ? 'all' : 'own';

  const attendance = (() => {
    if (hasElevatedPrivilege) return ATTENDANCE.ALL;
    if (isTeacher) return ATTENDANCE.OWN_ONGOING;
    return ATTENDANCE.NONE;
  })();

  return {
    canViewModule,
    canCreate,
    viewScope,
    // 'none' hides the tab, 'view' shows it read-only, 'edit' allows changes.
    resourcesAccess: contentAccess,
    quizzesAccess: contentAccess,
    // 'own' = only on the user's own classes, 'all' = every class. Pairs with the two
    // *Access values above: an EDIT grant still has to clear this.
    contentScope,
    attendance,
    // Raw flags, exposed so callers don't re-derive them from user.privilage.
    isSuperAdmin,
    isAdmin,
    isPlanner,
    isAttendanceMarker,
    isTeacher,
    isDataOperator,
    isOperation,
    isStudent,
    hasElevatedPrivilege,
  };
};

/**
 * Whether this user may add or replace a class's livestream link - the Others-mode
 * "..." menu on a class card.
 *
 * Deliberately NOT the Creation column, which is what this used to be. Supplying the
 * link to a class that already exists is not creating or restructuring a schedule,
 * and on an Others class the link is the teacher's own meeting: nobody else is in a
 * position to provide it. So creation rights carry it for every class, and a Teacher
 * carries it on their own class at any privilege level, the same ownership test
 * `canMarkAttendanceFor` uses.
 *
 * This is the one documented departure from the matrix above, which gives a bare
 * Teacher no Creation rights.
 */
export const canManageClassLink = (user, schedule) => {
  const { canCreate, isTeacher } = getSchedulePermissions(user);
  if (canCreate) return true;
  if (!isTeacher) return false;
  return (
    String(user?.id) === String(schedule?.teacher?.id) ||
    String(user?.id) === String(schedule?.teacherId)
  );
};

/**
 * Whether this user may mark attendance on a particular class.
 * `schedule` is the occurrence; `isOngoing` says whether it is live right now.
 */
export const canMarkAttendanceFor = (user, schedule, isOngoing) => {
  const { attendance } = getSchedulePermissions(user);
  if (attendance === ATTENDANCE.ALL) return true;
  if (attendance === ATTENDANCE.NONE) return false;
  // OWN_ONGOING: the bare Teacher case — their own class, and only while it runs.
  const ownsClass =
    String(user?.id) === String(schedule?.teacher?.id) ||
    String(user?.id) === String(schedule?.teacherId);
  return ownsClass && !!isOngoing;
};

export default getSchedulePermissions;
